/* @flow */
/**
 * Manages GAC reason code data and API actions for fetching, updating, and adding codes.
 */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import request from '../../services/request';
import { toggleSpinner } from '../dialogStates/dialogStatesSlice';
import { cryptoRand } from '../../helpers/common';
import { showForGACT2Vendor } from '../../helpers/envServer';

export const name = 'gacReasonCode';

function createInitialState() {
  return {
    dataId: '',
    dataRows: []
  };
}

export const initialState = createInitialState();

// SET_GAC_REASON_CODE
/**
 * Stores GAC reason code rows and refreshes the table data id.
 */
function setGACReasonCodes(state: Object, action: Object) {
  const dataId = cryptoRand().toString().slice(2, 10);
  const dataRows = action.payload.objects;
  return {
    ...state,
    dataRows,
    dataId
  };
}

/**
 * Collects GAC reason code reducers for slice creation.
 */
function createReducers() {
  return {
    setGACReasonCodes
  };
}

export const reducers = createReducers();
export const slice = createSlice({ name, initialState, reducers });

const actions = { ...slice.actions };

/**
 * Fetches GAC reason codes from getGACReasonCode and normalizes rows for display.
 */
function callGetGACReasonCodeAPI() {
  return createAsyncThunk(
    `${name}/callGetGACReasonCodeAPI`,
    (callback, { dispatch }) => {
      dispatch(toggleSpinner(true));
      request({
        api: 'getGACReasonCode',
        method: 'get'
      })
        .then((response) => {
          dispatch(toggleSpinner(false));
          console.log('callGetGACReasonCodeAPI response', response);
          const resData = response.data;
          resData.objects.forEach((row) => {
            const updatedRow = row;
            // Convert the boolean API value into the table's display label.
            updatedRow.isActive = row.isActive ? 'Active' : 'Inactive';
            if (showForGACT2Vendor && row.tier) {
              // T2 vendor mode forces all tiered rows to tier 2.
              updatedRow.tier = '2';
            }
          });
          dispatch(actions.setGACReasonCodes(resData));
          if (callback) callback(resData);
        })
        .catch((error) => {
          console.log('callGetGACReasonCodeAPI->error', error);
          dispatch(toggleSpinner(false));
          if (callback) callback(error);
        });
    }
  );
}

/**
 * Sends updated GAC reason code rows to the updateGacReasonCodes API.
 */
function callUpdateReasonCodeAPI() {
  return createAsyncThunk(
    `${name}/callUpdateReasonCodeAPI`,
    async ({
      payload,
      callback
    }, { dispatch }) => {
      dispatch(toggleSpinner(true));
      request({
        api: 'updateGacReasonCodes',
        method: 'put',
        data: {
          type: 'update',
          gacData: payload
        }
      })
        .then((response) => {
          dispatch(toggleSpinner(false));
          if (callback) callback(response.data);
        })
        .catch((error) => {
          console.log('callUpdateReasonCodeAPI error', error);
          dispatch(toggleSpinner(false));
          if (callback) callback(error);
        });
    }
  );
}

/**
 * Adds new GAC reason code rows through addGacReasonCodes after tier normalization.
 */
function callAddGacReasonCodeAPI() {
  return createAsyncThunk(
    `${name}/callAddGacReasonCodeAPI`,
    async ({
      payload,
      callback
    }, { dispatch }) => {
      dispatch(toggleSpinner(true));
      const updatedPayload = payload.map((item) => {
        if (showForGACT2Vendor) {
          if (item.tier) {
            // Preserve tiered rows but force the configured T2 tier value.
            return { ...item, tier: '2' };
          }
          const { tier, ...rest } = item;
          // Drop empty tier fields so the API receives only meaningful tier data.
          return rest;
        }
        return item;
      });
      request({
        api: 'addGacReasonCodes',
        method: 'post',
        data: { gacData: updatedPayload }
      })
        .then((response) => {
          dispatch(toggleSpinner(false));
          if (callback) callback(response.data);
        })
        .catch((error) => {
          console.log('callAddGacReasonCodeAPI->error', error);
          dispatch(toggleSpinner(false));
          if (callback) callback(error);
        });
    }
  );
}

/**
 * Creates GAC reason code async thunks exposed alongside slice actions.
 */
function createExtraActions() {
  return {
    callGetGACReasonCodeAPI: callGetGACReasonCodeAPI(),
    callUpdateReasonCodeAPI: callUpdateReasonCodeAPI(),
    callAddGacReasonCodeAPI: callAddGacReasonCodeAPI()
  };
}

export const extraActions = createExtraActions();

export const gacReasonCodeActions = { ...actions, ...extraActions };
export const gacReasonCodeReducer = slice.reducer;
